import React, { useState, useEffect } from 'react';
import { Discount, CreateDiscountDto, UpdateDiscountDto, DiscountFilters } from '../types/discount';
import { discountService } from '../services/discountService';
import { ApiResponse } from '../services/api';
import DiscountForm from '../components/Discount/DiscountForm';
import DiscountListing from '../components/Discount/DiscountListing';
import DiscountAnalytics from '../components/Discount/DiscountAnalytics';
import PromotionMonitor from '../components/Discount/PromotionMonitor';
import MetricCard from '../components/Common/MetricCard';

const DiscountManagement: React.FC = () => {
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState<Discount | null>(null);
  const [showAnalytics, setShowAnalytics] = useState<Discount | null>(null);
  const [activeTab, setActiveTab] = useState<'list' | 'monitor'>('list');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    loadDiscounts();
  }, []);

  const loadDiscounts = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await discountService.getAllDiscounts();
      
      if (response.success && response.data) {
        setDiscounts(response.data);
      } else {
        setError(response.error?.message || 'Failed to load discounts');
      }
    } catch (err) {
      setError('Failed to load discounts');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateDiscount = async (discountData: CreateDiscountDto) => {
    try {
      setIsSubmitting(true);
      const response = await discountService.createDiscount(discountData);
      
      if (response.success && response.data) {
        setDiscounts(prev => [response.data!, ...prev]);
        setShowForm(false);
        setEditingDiscount(null);
      } else {
        throw new Error(response.error?.message || 'Failed to create discount');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create discount');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateDiscount = async (discountData: CreateDiscountDto) => {
    if (!editingDiscount) return;

    try {
      setIsSubmitting(true);
      const response = await discountService.updateDiscount(editingDiscount._id, discountData);
      
      if (response.success && response.data) {
        setDiscounts(prev => 
          prev.map(d => d._id === editingDiscount._id ? response.data! : d)
        );
        setShowForm(false);
        setEditingDiscount(null);
      } else {
        throw new Error(response.error?.message || 'Failed to update discount');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update discount');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteDiscount = async (discountId: string) => {
    if (!window.confirm('Are you sure you want to delete this discount?')) {
      return;
    }

    try {
      const response = await discountService.deleteDiscount(discountId);
      
      if (response.success) {
        setDiscounts(prev => prev.filter(d => d._id !== discountId));
      } else {
        throw new Error(response.error?.message || 'Failed to delete discount');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete discount');
    }
  };

  const handleStatusChange = async (discountId: string, action: 'activate' | 'deactivate' | 'expire') => {
    try {
      let response: ApiResponse<Discount>;
      
      switch (action) {
        case 'activate':
          response = await discountService.activateDiscount(discountId);
          break;
        case 'deactivate':
          response = await discountService.deactivateDiscount(discountId);
          break;
        case 'expire':
          response = await discountService.expireDiscount(discountId);
          break;
        default:
          throw new Error(`Unknown action: ${action}`);
      }
      
      if (response.success && response.data) {
        setDiscounts(prev => 
          prev.map(d => d._id === discountId ? response.data! : d)
        );
      } else {
        throw new Error(response.error?.message || `Failed to ${action} discount`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${action} discount`);
    }
  };

  const handleEditDiscount = (discount: Discount) => {
    setEditingDiscount(discount);
    setShowForm(true);
  };

  const handleViewStats = (discount: Discount) => {
    setShowAnalytics(discount);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingDiscount(null);
  };

  const getDiscountSummary = () => {
    const active = discounts.filter(d => d.status === 'active').length;
    const inactive = discounts.filter(d => d.status === 'inactive').length;
    const expired = discounts.filter(d => d.status === 'expired').length;
    const totalUsage = discounts.reduce((sum, d) => sum + d.usageCount, 0);
    const totalBudgetUsed = discounts.reduce((sum, d) => sum + d.budgetUsed, 0);

    return { active, inactive, expired, totalUsage, totalBudgetUsed };
  };

  const summary = getDiscountSummary();

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-red-800">{error}</span>
            </div>
            <button
              onClick={loadDiscounts}
              className="text-red-600 hover:text-red-800 text-sm underline"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Discount Management</h1>
          <p className="text-gray-600">Create and manage promotional discounts</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Create Discount
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <MetricCard
          title="Total Discounts"
          value={discounts.length.toString()}
        />
        <MetricCard
          title="Active"
          value={summary.active.toString()}
          change={summary.active > 0 ? { value: summary.active, type: 'increase' } : undefined}
        />
        <MetricCard
          title="Inactive"
          value={summary.inactive.toString()}
        />
        <MetricCard
          title="Total Usage"
          value={summary.totalUsage.toString()}
        />
        <MetricCard
          title="Budget Used"
          value={`$${summary.totalBudgetUsed.toFixed(2)}`}
        />
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('list')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'list'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            All Discounts
          </button>
          <button
            onClick={() => setActiveTab('monitor')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'monitor'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Promotion Monitor
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'list' && (
        <DiscountListing
          discounts={discounts}
          onEdit={handleEditDiscount}
          onDelete={handleDeleteDiscount}
          onStatusChange={handleStatusChange}
          onViewStats={handleViewStats}
          isLoading={isLoading}
        />
      )}

      {activeTab === 'monitor' && (
        <PromotionMonitor
          discounts={discounts}
          onRefresh={loadDiscounts}
        />
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <DiscountForm
              discount={editingDiscount}
              onSubmit={editingDiscount ? handleUpdateDiscount : handleCreateDiscount}
              onCancel={handleFormCancel}
              isLoading={isSubmitting}
            />
          </div>
        </div>
      )}

      {/* Analytics Modal */}
      {showAnalytics && (
        <DiscountAnalytics
          discount={showAnalytics}
          onClose={() => setShowAnalytics(null)}
        />
      )}
    </div>
  );
};

export default DiscountManagement;